import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { IconButton } from 'src/components/Buttons';
import { EventDateDisplay } from 'src/components/Events/EventInfo';
import { useAppContext } from 'src/contexts/AppContext';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_SM,
  COLUMN_DEFAULT_SIZE_XL,
  COLUMN_DEFAULT_SIZE_XXL,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { useEventDiscoveryContext } from 'src/contexts/EventDiscoveryContext/EventDiscoveryContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { vars } from 'src/core/themes';
import { Stack, TooltipPopover } from 'src/core/ui';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOption, FormatOptionEntries } from 'src/modals/EditTableColumns';
import { CustomColumnName } from 'src/modals/InventoryTableColumns/CustomColumnName';
import { FullPageIcon } from 'src/svgs';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { EventDisplayCell } from 'src/tables/EventsTable/configs/EventDisplayCell';
import { LocationDisplayCell } from 'src/tables/EventsTable/configs/LocationDisplayCell';
import { VenueDisplayCell } from 'src/tables/EventsTable/configs/VenueDisplayCell';
import { CustomNumericCell } from 'src/tables/ListingTable/configs/CustomNumericCell';
import { TableShimmeringDiv } from 'src/tables/Table';
import {
  DemandDashboardEventIdQueryParam,
  DemandDashboardIsOnSaleEventQueryParam,
} from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  compareEventsByDate,
  compareEventsByDateStr,
  compareEventsByLocation,
  compareEventsByName,
  compareEventsByVenue,
  getDescription,
  getFormattedEventName,
} from 'src/utils/eventWithDataUtils';
import {
  getGroupingLabelForString,
  getGroupLabelForDates,
  getGroupLabelForNumber,
} from 'src/utils/groupingUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { sortMoney } from 'src/utils/tableUtils';
import {
  SECTION_TYPE_TO_PRECISION_SETTING,
  SectionType,
} from 'src/utils/types/sectionType';
import {
  DateTimeRange,
  Event,
  Feature,
  UserSetting,
} from 'src/WebApiController';

import { evaluateOnSaleEventFormula } from './OnSaleDiscoveryCustomColumnUtils';
import {
  CustomOnSaleEventColumn,
  OnSaleEventTableColumnId,
  OnSaleEventWithMetrics,
} from './OnSaleDiscoveryView.types';

const QUANTITY_COLUMN_MIN_SIZE = 50;
const QUANTITY_COLUMN_MAX_SIZE = 80;
const COST_COLUMN_MIN_SIZE = 50;
const COST_COLUMN_MAX_SIZE = 80;

export const groupLabelForRecentVisit = (range?: number[]): string => {
  const value = range?.[0] ?? 0;
  const hundreds = value / 100;
  if (hundreds <= 1) {
    return '0 to 100';
  }
  const length = String(value).length;
  const from = 10 ** (length - 1);
  const fromLabel = from.toLocaleString();
  const to = (10 ** length).toLocaleString();
  return `${fromLabel} to ${to}`;
};

export const customColumnDef = (
  columnId: string,
  formula?: string | null
): ColumnDef<OnSaleEventWithMetrics | null> => ({
  id: columnId,
  header: ({ table }) => (
    <TableHeaderCellDiv align="right">
      <CustomColumnName
        id={columnId}
        sectionType={
          table.options.meta?.sectionType ?? SectionType.OnSaleEvents
        }
      />
    </TableHeaderCellDiv>
  ),
  enableColumnFilter: false,
  size: COLUMN_DEFAULT_SIZE_LG,
  minSize: COLUMN_MIN_SIZE,
  maxSize: COLUMN_MAX_SIZE,
  accessorFn: (data) => {
    return formula && data
      ? (evaluateOnSaleEventFormula(formula, data) ?? null)
      : null;
  },
  cell: function Cell({ getValue, row: { original }, table }) {
    const { loginContext } = useAppContext();
    const { value: storedInventoryColumnNumberPrecisions = {} } =
      useServerUserSetting<FormatOptionEntries>({
        id: SECTION_TYPE_TO_PRECISION_SETTING[
          table.options.meta?.sectionType ?? SectionType.OnSaleEvents
        ],
      });
    const { value: customColumns = [] } = useServerUserSetting<
      CustomOnSaleEventColumn[]
    >({
      id: UserSetting.OnSaleEventCustomColumns,
    });

    const userDefinedPrecision =
      storedInventoryColumnNumberPrecisions[columnId];

    return (
      <CustomNumericCell
        columnId={columnId}
        userDefinedPrecision={userDefinedPrecision}
        value={getValue<number | null>()}
        isLoading={Boolean(!original || !original.metrics)}
        shimmeringDivHeight={vars.spacing.xxl}
        sectionType={
          table.options.meta?.sectionType ?? SectionType.OnSaleEvents
        }
        matchingCustomColumn={customColumns.find((c) => c.id === columnId)}
        currencyCode={loginContext?.user?.activeAccount?.currencyCode ?? 'USD'}
      />
    );
  },
});

export const ON_SALE_EVENTS_TABLE_COLUMNS_CONFIG: Partial<
  Record<OnSaleEventTableColumnId, ColumnDef<OnSaleEventWithMetrics | null>>
> = {
  [OnSaleEventTableColumnId.Checkbox]: {
    id: OnSaleEventTableColumnId.Checkbox,
    header: '',
    size: COLUMN_DEFAULT_SIZE_SM,
    minSize: COLUMN_DEFAULT_SIZE_SM,
    maxSize: COLUMN_DEFAULT_SIZE_SM,
    enableSorting: false,
    enableResizing: false,
    cell: function Cell({ row: { original, getIsSelected } }) {
      const { setShiftKeyGroupSelectionState, clearShiftKeySelection } =
        useMultiSelectionContext();
      const isSelected = getIsSelected();

      if (!original?.event) {
        return <TableShimmeringDiv />;
      }

      return (
        <TableCellDiv
          align="left"
          showTooltip={false}
          style={{ paddingLeft: vars.spacing.sm }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => {
              const isCheckedPrev = isSelected;
              if (!isCheckedPrev) {
                setShiftKeyGroupSelectionState(original.event.virtId);
              } else {
                clearShiftKeySelection();
              }
            }}
          />
        </TableCellDiv>
      );
    },
  },
  [OnSaleEventTableColumnId.Event]: {
    id: OnSaleEventTableColumnId.Event,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_XXL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data,
    cell: function Cell({ getValue, row: { original } }) {
      const { metrics, ...rest } = getValue<OnSaleEventWithMetrics>();
      if (!original?.event) {
        return <TableShimmeringDiv />;
      }

      return <EventDisplayCell {...rest} forceDisplayEventName />;
    },
    sortingFn: compareEventsByName,
    getGroupingValue: (row) => {
      const eventName = getFormattedEventName({
        event: row?.event,
        performer: row?.performer,
        venue: row?.venue,
        useShortName: true,
      }).join(' ');
      return getGroupingLabelForString(eventName);
    },
  },
  [OnSaleEventTableColumnId.InferredPrimary]: {
    id: OnSaleEventTableColumnId.InferredPrimary,
    header: () => (
      <TableHeaderCellDiv align="left" showTooltip={false}>
        <Stack alignItems="center" gap="s">
          <Content id={ContentId.Primary} />
          <TooltipPopover
            triggerContent={
              <InfoOutlineIcon withHoverEffect size={vars.iconSize.s} />
            }
          >
            <Content id={ContentId.InferredPrimary} />
          </TooltipPopover>
        </Stack>
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data,
    cell: function Cell({ getValue, row: { original } }) {
      const { eventAttributes } = getValue<OnSaleEventWithMetrics>();
      const naText = useContent(ContentId.NA);
      if (!original?.event || original?.eventAttributes === undefined) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="left">
          {eventAttributes?.inferredPrimary
            ? eventAttributes.inferredPrimary
            : naText}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupingLabelForString(item?.eventAttributes?.inferredPrimary ?? ''),
  },
  [OnSaleEventTableColumnId.Venue]: {
    id: OnSaleEventTableColumnId.Venue,
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Venue} />
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_XL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data,
    cell: function Cell({ getValue, row: { original } }) {
      const { metrics, ...rest } = getValue<OnSaleEventWithMetrics>();
      if (!original?.event) {
        return <TableShimmeringDiv />;
      }
      return <VenueDisplayCell {...rest} />;
    },
    sortingFn: compareEventsByVenue,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupingLabelForString(item?.venue?.name ?? ''),
  },
  [OnSaleEventTableColumnId.Capacity]: {
    id: OnSaleEventTableColumnId.Capacity,
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Capacity} />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data,
    cell: function Cell({ getValue, row: { original } }) {
      const { venue } = getValue<OnSaleEventWithMetrics>();
      const naText = useContent(ContentId.NA);
      if (!original?.event) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {!venue?.cap
            ? naText
            : applyNumberFormatting({
                precision: FormatOption.Whole,
                inputNumber: venue?.cap,
              })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.venue?.cap ?? 0),
  },
  [OnSaleEventTableColumnId.Location]: {
    id: OnSaleEventTableColumnId.Location,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_XL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data,
    cell: function Cell({ getValue, row: { original } }) {
      const { metrics, ...rest } = getValue<OnSaleEventWithMetrics>();
      if (!original?.event) {
        return <TableShimmeringDiv />;
      }
      return <LocationDisplayCell {...rest} />;
    },
    sortingFn: compareEventsByLocation,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupingLabelForString(getDescription(item?.venue) ?? ''),
  },
  [OnSaleEventTableColumnId.OnSaleDate]: {
    id: OnSaleEventTableColumnId.OnSaleDate,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.event?.rawOnSaleDate,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;

      const value = getValue<string | undefined>();
      const { userDefinedPrecision = FormatOption.DateTime_Month_DD_YY_Time } =
        useColumnUserSetting(columnId, SectionType.OnSaleEvents);

      if (!original?.event) {
        return <TableShimmeringDiv />;
      }

      const date = applyNumberFormatting({
        precision: userDefinedPrecision,
        inputNumber: value,
        isDateTime: true,
      }) as string;

      return (
        <TableCellDiv align="left">
          {userDefinedPrecision === FormatOption.DateTime_MonthDD_Time ? (
            <EventDateDisplay
              eventDateString={value ?? undefined}
              eventEndDateString={undefined}
              showEventDateTbd={true}
              eventSubDateFormat={' - h:mm aaa, eee'}
            />
          ) : (
            <div style={{ textTransform: 'none' }}>{date}</div>
          )}
        </TableCellDiv>
      );
    },
    sortingFn: compareEventsByDateStr,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForDates(item?.event.rawOnSaleDate),
  },
  [OnSaleEventTableColumnId.Date]: {
    id: OnSaleEventTableColumnId.Date,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.event?.dates,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;

      const value = getValue<DateTimeRange | undefined>();
      const { userDefinedPrecision = FormatOption.DateTime_Month_DD_YY_Time } =
        useColumnUserSetting(columnId, SectionType.OnSaleEvents);

      if (!original?.event) {
        return <TableShimmeringDiv />;
      }

      const date = applyNumberFormatting({
        precision: userDefinedPrecision,
        inputNumber: value?.start,
        isDateTime: true,
      }) as string;

      return (
        <TableCellDiv align="left">
          {userDefinedPrecision === FormatOption.DateTime_MonthDD_Time ? (
            <EventDateDisplay
              eventDateString={value?.start ?? undefined}
              eventEndDateString={value?.end ?? undefined}
              showEventDateTbd={true}
              eventSubDateFormat={' - h:mm aaa, eee'}
            />
          ) : (
            <div style={{ textTransform: 'none' }}>{date}</div>
          )}
        </TableCellDiv>
      );
    },
    sortingFn: compareEventsByDate,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForDates(item?.event.dates.start),
  },
  [OnSaleEventTableColumnId.RawPreSaleDate]: {
    id: OnSaleEventTableColumnId.RawPreSaleDate,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.event?.rawPreSaleDate,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;

      const value = getValue<string | undefined>();
      const { userDefinedPrecision = FormatOption.DateTime_Month_DD_YY_Time } =
        useColumnUserSetting(columnId, SectionType.OnSaleEvents);

      if (!original?.event) {
        return <TableShimmeringDiv />;
      }

      const date = applyNumberFormatting({
        precision: userDefinedPrecision,
        inputNumber: value,
        isDateTime: true,
      }) as string;

      return (
        <TableCellDiv align="left">
          {userDefinedPrecision === FormatOption.DateTime_MonthDD_Time ? (
            <EventDateDisplay
              eventDateString={value ?? undefined}
              eventEndDateString={undefined}
              showEventDateTbd={true}
              eventSubDateFormat={' - h:mm aaa, eee'}
            />
          ) : (
            <div style={{ textTransform: 'none' }}>{date}</div>
          )}
        </TableCellDiv>
      );
    },
    sortingFn: compareEventsByDateStr,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForDates(item?.event.rawPreSaleDate),
  },
  [OnSaleEventTableColumnId.ListingCount]: {
    id: OnSaleEventTableColumnId.ListingCount,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.ltgCnt,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.ltgCnt ?? 0),
  },
  [OnSaleEventTableColumnId.TicketCount]: {
    id: OnSaleEventTableColumnId.TicketCount,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.tktCnt,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.tktCnt ?? 0),
  },
  [OnSaleEventTableColumnId.UnitNetProceedsMin]: {
    id: OnSaleEventTableColumnId.UnitNetProceedsMin,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.minUNP,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;

      const value = getValue<number | null>();
      const { loginContext } = useAppContext();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
            currencyCode:
              loginContext?.user?.activeAccount?.currencyCode ?? 'USD',
          })}
        </TableCellDiv>
      );
    },
    sortingFn: sortMoney<OnSaleEventWithMetrics>,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.minUNP ?? 0, '$'),
  },
  [OnSaleEventTableColumnId.UnitNetProceedsP25]: {
    id: OnSaleEventTableColumnId.UnitNetProceedsP25,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.p25UNP,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;

      const value = getValue<number | null>();
      const { loginContext } = useAppContext();

      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.Events
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
            currencyCode:
              loginContext?.user?.activeAccount?.currencyCode ?? 'USD',
          })}
        </TableCellDiv>
      );
    },
    sortingFn: sortMoney<OnSaleEventWithMetrics>,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.p25UNP ?? 0, '$'),
  },
  [OnSaleEventTableColumnId.UnitNetProceedsP50]: {
    id: OnSaleEventTableColumnId.UnitNetProceedsP50,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.p50UNP,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;

      const value = getValue<number | null>();
      const { loginContext } = useAppContext();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
            currencyCode:
              loginContext?.user?.activeAccount?.currencyCode ?? 'USD',
          })}
        </TableCellDiv>
      );
    },
    sortingFn: sortMoney<OnSaleEventWithMetrics>,
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.p50UNP ?? 0, '$'),
  },
  [OnSaleEventTableColumnId.RecentVisits]: {
    id: OnSaleEventTableColumnId.RecentVisits,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={
            ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
              columnId as OnSaleEventTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.vstIdx,
    cell: function Cell({ row: { original }, column }) {
      const columnId = column.id;
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );

      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }

      const { vstBkt } = original.metrics;
      if (vstBkt == null || vstBkt[0] == null || vstBkt[1] == null) {
        return null;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: vstBkt[0],
          })}
          ~
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: vstBkt[1],
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      groupLabelForRecentVisit(item?.metrics?.vstBkt),
  },
  [OnSaleEventTableColumnId.VisitsLastHour]: {
    id: OnSaleEventTableColumnId.VisitsLastHour,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <FormatContent id={FormatContentId.VisitsLastNHour} params={['1']} />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventVisits?.lastHour,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.eventVisits?.lastHour ?? 0),
  },
  [OnSaleEventTableColumnId.VisitsLastDay]: {
    id: OnSaleEventTableColumnId.VisitsLastDay,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <FormatContent id={FormatContentId.VisitsLastNHour} params={['24']} />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventVisits?.lastDay,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.eventVisits?.lastDay ?? 0),
  },
  [OnSaleEventTableColumnId.VisitsLast3Days]: {
    id: OnSaleEventTableColumnId.VisitsLast3Days,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <FormatContent id={FormatContentId.VisitsLastNDay} params={['3']} />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventVisits?.lastThreeDays,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.eventVisits?.lastThreeDays ?? 0),
  },
  [OnSaleEventTableColumnId.VisitsLast7Days]: {
    id: OnSaleEventTableColumnId.VisitsLast7Days,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <FormatContent id={FormatContentId.VisitsLastNDay} params={['7']} />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventVisits?.lastSevenDays,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.eventVisits?.lastSevenDays ?? 0),
  },

  [OnSaleEventTableColumnId.TransactionsLastHour]: {
    id: OnSaleEventTableColumnId.TransactionsLastHour,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <FormatContent
          id={FormatContentId.TransactionsLastNHour}
          params={['1']}
        />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventTransactions?.lastHour,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.eventTransactions?.lastHour ?? 0),
  },
  [OnSaleEventTableColumnId.TransactionsLastDay]: {
    id: OnSaleEventTableColumnId.TransactionsLastDay,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <FormatContent
          id={FormatContentId.TransactionsLastNHour}
          params={['24']}
        />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventTransactions?.lastDay,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(item?.metrics?.eventTransactions?.lastDay ?? 0),
  },
  [OnSaleEventTableColumnId.TransactionsLast3Days]: {
    id: OnSaleEventTableColumnId.TransactionsLast3Days,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="left">
        <FormatContent
          id={FormatContentId.TransactionsLastNDay}
          params={['3']}
        />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventTransactions?.lastThreeDays,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(
        item?.metrics?.eventTransactions?.lastThreeDays ?? 0
      ),
  },
  [OnSaleEventTableColumnId.TransactionsLast7Days]: {
    id: OnSaleEventTableColumnId.TransactionsLast7Days,
    header: ({ column: { id: columnId } }) => (
      <TableHeaderCellDiv align="right">
        <FormatContent
          id={FormatContentId.TransactionsLastNDay}
          params={['7']}
        />
      </TableHeaderCellDiv>
    ),
    minSize: QUANTITY_COLUMN_MIN_SIZE,
    maxSize: QUANTITY_COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.metrics?.eventTransactions?.lastSevenDays,
    cell: function Cell({ getValue, row: { original }, column }) {
      const columnId = column.id;
      const value = getValue<number | undefined>();
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        SectionType.OnSaleEvents
      );
      if (!original?.metrics) {
        return <TableShimmeringDiv />;
      }
      return (
        <TableCellDiv align="right">
          {applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          })}
        </TableCellDiv>
      );
    },
    getGroupingValue: (item: OnSaleEventWithMetrics | null) =>
      getGroupLabelForNumber(
        item?.metrics?.eventTransactions?.lastSevenDays ?? 0
      ),
  },
  [OnSaleEventTableColumnId.Action]: {
    id: OnSaleEventTableColumnId.Action,
    enableSorting: false,
    enableResizing: true,
    header: function Cell() {
      return null;
    },
    size: COLUMN_DEFAULT_SIZE_BASE,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.event ?? null,
    cell: function Cell({ getValue, row: { original } }) {
      const value = getValue<Event>();
      if (!original?.event) {
        return <TableShimmeringDiv />;
      }
      return <OnSaleEventActionsCell event={value} />;
    },
  },
};

export const OnSaleEventActionsCell = ({ event }: { event: Event }) => {
  const { onEventClick } = useEventDiscoveryContext();
  const hasOnSaleEventDetailsViewFeature = useUserHasFeature(
    Feature.OnSaleEventDetailsView
  );
  const hasEventDiscoveryInInventoryViewFeature = useUserHasFeature(
    Feature.EventDiscoveryInInventoryView
  );

  const eventUrl = useMemo(() => {
    if (hasEventDiscoveryInInventoryViewFeature) {
      return `/inventory/event?${DemandDashboardEventIdQueryParam}=${event.virtId}&${DemandDashboardIsOnSaleEventQueryParam}=true`;
    }
    if (hasOnSaleEventDetailsViewFeature) {
      return `/discovery/event?${DemandDashboardEventIdQueryParam}=${event.virtId}`;
    }
    return `/inventory/event/v2?${DemandDashboardEventIdQueryParam}=${event.virtId}&${DemandDashboardIsOnSaleEventQueryParam}=true`;
  }, [
    event.virtId,
    hasEventDiscoveryInInventoryViewFeature,
    hasOnSaleEventDetailsViewFeature,
  ]);

  return (
    <TableCellDiv align="right" showTooltip={false}>
      <Link
        to={eventUrl}
        style={{ height: vars.iconSize.l }}
        onClick={(e) => {
          if (e.button === 0 && e.getModifierState('Control') === false) {
            // Perform like a button click when it's a left-click (and no Ctrl)
            // Else the click will be whatever <a /> does
            e.preventDefault();
            e.stopPropagation();

            if (event?.viagId) {
              onEventClick(event.viagId);
            }
          }
        }}
      >
        <IconButton
          icon={
            <FullPageIcon
              fill={vars.color.textWarning}
              size={vars.iconSize.xs}
              withHoverEffect
            />
          }
          titleContentId={ContentId.OpenEventPageV2InDevelopment}
        />
      </Link>
    </TableCellDiv>
  );
};
